import React, { useState, useEffect, useRef } from 'react';
import { submitOnBehalfOf, searchPatients } from '../lib/demographic';

/**
 * OnBehalfOfEntryForm
 *
 * Clinician/Admin entry point for a patient who has no current
 * demographic_submission. Submits directly to pending_verification —
 * no draft/submitted intermediate step. The submitting clinician may
 * subsequently verify their own entry from the queue, same as any
 * other pending_verification row.
 *
 * Target patient selection: debounced search against GET /users
 * (role=patient, tenant-scoped server-side), replacing the earlier
 * raw platform_user_id text input. If a selected target already has
 * patient_id set (a canonical patient record exists), submission is
 * still attempted — the backend's existing 409
 * ("already has a current submission") is the source of truth, not a
 * client-side guess about submission state, since this endpoint
 * doesn't expose demographic_submission status.
 */
export default function OnBehalfOfEntryForm({ onBack, onResolved }) {
  const [form, setForm] = useState({
    platform_user_id: '',
    first_name: '',
    last_name: '',
    date_of_birth: '',
    sex: '',
    external_mrn: '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const [selectedPatient, setSelectedPatient] = useState(null); // { platform_user_id, display_name, email, patient_id } | null
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (query.trim().length < 2) {
      setResults([]);
      setSearching(false);
      return;
    }

    setSearching(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const data = await searchPatients(query.trim());
        setResults(data);
      } catch (err) {
        console.error('[OnBehalfOfEntryForm] patient search failed:', err?.message || err);
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);

    return () => clearTimeout(debounceRef.current);
  }, [query]);

  function update(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  function selectPatient(patient) {
    setSelectedPatient(patient);
    setForm((f) => ({ ...f, platform_user_id: patient.platform_user_id }));
    setQuery('');
    setResults([]);
    setShowDropdown(false);
  }

  function clearSelection() {
    setSelectedPatient(null);
    setForm((f) => ({ ...f, platform_user_id: '' }));
  }

  async function handleSubmit() {
    if (!form.platform_user_id || !form.first_name || !form.last_name || !form.date_of_birth || !form.sex) {
      setError('Patient, first name, last name, date of birth, and sex are required.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await submitOnBehalfOf(form);
      onResolved();
    } catch (err) {
      setError(err.response?.data?.message || 'Submission failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-6">
      <button onClick={onBack} className="text-xs text-slate-400 hover:text-white">
        ← Back to queue
      </button>

      <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-6">
        <h3 className="text-lg font-bold text-white mb-4">Enter demographics for patient</h3>

        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="col-span-2">
            <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
              Patient
            </label>

            {selectedPatient ? (
              <div className="mt-1 flex items-center justify-between rounded-lg border border-emerald-700/50 bg-emerald-500/10 px-3 py-2">
                <div>
                  <p className="text-sm font-semibold text-white">
                    {selectedPatient.display_name || selectedPatient.email}
                  </p>
                  <p className="text-xs text-slate-400">{selectedPatient.email}</p>
                  {selectedPatient.patient_id && (
                    <p className="text-[10px] text-amber-400 mt-0.5">
                      Already has a linked patient record — submission may be rejected if a
                      current demographic record already exists.
                    </p>
                  )}
                </div>
                <button
                  onClick={clearSelection}
                  className="text-xs text-slate-400 hover:text-white ml-4 flex-shrink-0"
                >
                  Change
                </button>
              </div>
            ) : (
              <div className="relative mt-1">
                <input
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setShowDropdown(true);
                  }}
                  onFocus={() => setShowDropdown(true)}
                  placeholder="Search patient by name or email…"
                  className="w-full rounded-lg border border-slate-700 bg-slate-800 text-white text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />

                {showDropdown && query.trim().length >= 2 && (
                  <div className="absolute z-10 mt-1 w-full rounded-lg border border-slate-700 bg-slate-800 shadow-lg max-h-64 overflow-y-auto">
                    {searching && (
                      <p className="px-3 py-2 text-xs text-slate-500">Searching…</p>
                    )}
                    {!searching && results.length === 0 && (
                      <p className="px-3 py-2 text-xs text-slate-500">No patients found.</p>
                    )}
                    {!searching &&
                      results.map((r) => (
                        <button
                          key={r.platform_user_id}
                          onClick={() => selectPatient(r)}
                          className="w-full text-left px-3 py-2 hover:bg-slate-700 transition"
                        >
                          <p className="text-sm text-white">{r.display_name || r.email}</p>
                          <p className="text-xs text-slate-500">{r.email}</p>
                        </button>
                      ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div>
            <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">First name</label>
            <input value={form.first_name} onChange={(e) => update('first_name', e.target.value)}
              className="w-full rounded-lg border border-slate-700 bg-slate-800 text-white text-sm px-3 py-2 mt-1 focus:outline-none focus:ring-2 focus:ring-emerald-500" />
          </div>
          <div>
            <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Last name</label>
            <input value={form.last_name} onChange={(e) => update('last_name', e.target.value)}
              className="w-full rounded-lg border border-slate-700 bg-slate-800 text-white text-sm px-3 py-2 mt-1 focus:outline-none focus:ring-2 focus:ring-emerald-500" />
          </div>
          <div>
            <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Date of birth</label>
            <input type="date" value={form.date_of_birth} onChange={(e) => update('date_of_birth', e.target.value)}
              className="w-full rounded-lg border border-slate-700 bg-slate-800 text-white text-sm px-3 py-2 mt-1 focus:outline-none focus:ring-2 focus:ring-emerald-500" />
          </div>
          <div>
            <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Sex</label>
            <input value={form.sex} onChange={(e) => update('sex', e.target.value)}
              className="w-full rounded-lg border border-slate-700 bg-slate-800 text-white text-sm px-3 py-2 mt-1 focus:outline-none focus:ring-2 focus:ring-emerald-500" />
          </div>
          <div>
            <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">External MRN</label>
            <input value={form.external_mrn} onChange={(e) => update('external_mrn', e.target.value)}
              className="w-full rounded-lg border border-slate-700 bg-slate-800 text-white text-sm px-3 py-2 mt-1 focus:outline-none focus:ring-2 focus:ring-emerald-500" />
          </div>
        </div>

        {error && (
          <p className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2 mb-4">
            {error}
          </p>
        )}

        <button
          onClick={handleSubmit}
          disabled={busy}
          className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-bold py-2.5 rounded-xl transition"
        >
          {busy ? 'Submitting…' : 'Submit for Verification'}
        </button>
      </div>
    </div>
  );
}